import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import BlockRenderer from "@/cms/BlockRenderer";
import { useSiteContent } from "@/cms/useSiteContent";
import { getCustomPage } from "@/services/customPagesService";
import PageNotFound from "@/lib/PageNotFound";

/** Public renderer for every admin-created custom page (Edit Site CMS
 * "+ New Page"), reached at /page/:slug. Mirrors AboutUs.jsx/ContactUs.jsx
 * — same useSiteContent + BlockRenderer pair — except the pageKey (the
 * slug) is only known at request time, and existing at all is only
 * confirmed by a customPages registry lookup (a deleted page's old
 * siteContent blocks are left in Firestore but no longer reachable). */
export default function CustomPage() {
  const { slug } = useParams();
  const [pageExists, setPageExists] = useState(null); // null = loading, false = 404
  const { blocks, loading: blocksLoading } = useSiteContent(slug);

  useEffect(() => {
    let cancelled = false;
    setPageExists(null);
    (async () => {
      try {
        const meta = await getCustomPage(slug);
        if (!cancelled) {
          setPageExists(!!meta);
          if (meta?.title) document.title = meta.title;
        }
      } catch (err) {
        console.error("Failed to load custom page:", err);
        if (!cancelled) setPageExists(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (pageExists === null || blocksLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 gap-2 bg-[#0B2421]">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading...
      </div>
    );
  }

  if (!pageExists) return <PageNotFound />;

  return (
    <div className="min-h-screen bg-[#0B2421]">
      <BlockRenderer blocks={blocks} />
    </div>
  );
}
